import { CircularProgress } from "@material-ui/core";
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useEffect, useState } from "react";
import {
  LuArrowRight,
  LuFileText,
  LuPackage,
  LuShip
} from "react-icons/lu";

const T = {
  dark: "#081f35",
  primary: "#313C4C",
  light: "#dfe6ed",
  bg: "#f8fafc",
};

const C = {
  teal: {
    bg: "#E1F5EE",
    border: "#0F6E56",
    text: "#085041",
    dot: "#1D9E75",
    icon: "#0F6E56",
  },
  purple: {
    bg: "#EEEDFE",
    border: "#534AB7",
    text: "#3C3489",
    dot: "#7F77DD",
    icon: "#534AB7",
  },
  coral: {
    bg: "#FAECE7",
    border: "#993C1D",
    text: "#712B13",
    dot: "#D85A30",
    icon: "#993C1D",
  },
  amber: {
    bg: "#FAEEDA",
    border: "#854F0B",
    text: "#633806",
    dot: "#BA7517",
    icon: "#854F0B",
  },
  gray: {
    bg: "#F1EFE8",
    border: "#5F5E5A",
    text: "#2C2C2A",
    dot: "#888780",
    icon: "#5F5E5A",
  },
  green: {
    bg: "#EAF3DE",
    border: "#3B6D11",
    text: "#27500A",
    dot: "#639922",
    icon: "#3B6D11",
  },
};


const TARGET_HANDLE = {
  position: "absolute",
  left: "50%",
  top: 0,
  transform: "translate(-50%, -50%)",
  opacity: 0,
  pointerEvents: "none",
};

const SOURCE_HANDLE = {
  position: "absolute",
  left: "50%",
  bottom: 0,
  top: "auto",
  transform: "translate(-50%, 50%)",
  opacity: 0,
  pointerEvents: "none",
};

const ICON_MAP = {
  "Create Cargo": LuPackage,
  "Create Vessel": LuShip,
  "Create Cargo-Cargo Estimation": LuFileText,
  "Create Vessel-Cargo Estimation": LuFileText,
  "Create Vessel-Vessel Estimation": LuFileText,
  "Create My Matrix": LuFileText,
  "Create Cargo Analysis": LuFileText,
  "Cargo-Cargo Est": LuFileText,
  "Vessel-Vessel Est": LuFileText,
  "All Estimation": LuFileText,
  "Cargo Analysis": LuFileText,
};

const BADGE_MAP = {
  "Create Cargo": { label: "Resource", color: C.teal },
  "Create Vessel": { label: "Resource", color: C.coral },
  "Create Cargo-Cargo Estimation": { label: "Estimate", color: C.teal },
  "Create Vessel-Cargo Estimation": { label: "Estimate", color: C.purple },
  "Create Vessel-Vessel Estimation": { label: "Estimate", color: C.coral },
  "Create My Matrix": { label: "Matrix", color: C.purple },
  "Create Cargo Analysis": { label: "Analysis", color: C.amber },
  "Cargo-Cargo Est": { label: "Sub-Tab", color: C.teal },
  "Vessel-Vessel Est": { label: "Sub-Tab", color: C.coral },
  "All Estimation": { label: "Sub-Tab", color: C.purple },
  "Cargo Analysis": { label: "Sub-Tab", color: C.amber },
};

const STEP_MAP = {
  "Create Cargo": "01",
  "Create Vessel": "01",
  "Create Cargo-Cargo Estimation": "02",
  "Create Vessel-Cargo Estimation": "02",
  "Create Vessel-Vessel Estimation": "02",
  "Create My Matrix": "02",
  "Create Cargo Analysis": "02",
  "Cargo-Cargo Est": "03",
  "Vessel-Vessel Est": "03",
  "All Estimation": "03",
  "Cargo Analysis": "03",
};

const ClickHereLink = ({ accent, hov, small = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 4,
      marginTop: small ? 6 : 8,
      fontSize: small ? 7.5 : 8,
      fontWeight: 700,
      color: hov ? accent : "#94a3b8",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      transition: "color 0.2s ease",
      cursor: "pointer",
    }}
  >
    <span>Click here</span>
    <LuArrowRight
      size={small ? 7 : 8}
      color={hov ? accent : "#94a3b8"}
      strokeWidth={2.5}
      style={{ transition: "color 0.2s ease", flexShrink: 0 }}
    />
  </div>
);

const GroupNode = ({ data }) => {
  const [hov, setHov] = useState(false);
  const accent = data.color || T.primary;
  const IconComp = ICON_MAP[data.title];
  const badge = BADGE_MAP[data.title];
  const stepNum = STEP_MAP[data.title];

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: data.width,
        minHeight: data.height,
        border: "1.5px solid #e2e8f0",
        borderTop: `3px solid ${accent}`,
        borderRadius: 16,
        boxShadow:
          hov && data.route
            ? `0 0 0 1.5px ${accent}, 0 16px 32px -10px ${accent}30, 0 2px 10px -3px ${accent}14`
            : "0 2px 14px -4px rgba(0,0,0,0.07)",
        background:
          hov && data.route
            ? "#ffffff"
            : "linear-gradient(155deg,#ffffff 0%,#f4f8fc 100%)",
        padding: "14px 16px 15px",
        boxSizing: "border-box",
        position: "relative",
        cursor: data.route ? "pointer" : "default",
        transition:
          "box-shadow 0.22s ease, background 0.22s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        transform:
          hov && data.route
            ? "translateY(-4px) scale(1.01)"
            : "translateY(0) scale(1)",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        overflow: "hidden",
      }}
    >
      <Handle type="target" position={Position.Top} style={TARGET_HANDLE} />

      {stepNum && (
        <span
          style={{
            position: "absolute",
            bottom: -4,
            right: 8,
            fontSize: 52,
            fontWeight: 900,
            color: `${accent}08`,
            lineHeight: 1,
            letterSpacing: "-2px",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {stepNum}
        </span>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              background: `linear-gradient(135deg,${accent}18 0%,${accent}0c 100%)`,
              border: `1px solid ${accent}20`,
              borderRadius: 10,
              flexShrink: 0,
              boxShadow: `0 2px 6px -1px ${accent}16`,
            }}
          >
            {IconComp && (
              <IconComp size={16} color={accent} strokeWidth={2.2} />
            )}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 800,
              color: T.dark,
              letterSpacing: "-0.1px",
              lineHeight: 1.2,
            }}
          >
            {data.title}
          </span>
        </div>

        {badge && (
          <span
            style={{
              fontSize: 7.5,
              fontWeight: 800,
              color: badge.color.text,
              background: badge.color.bg,
              border: `1px solid ${badge.color.border}26`,
              padding: "2px 7px",
              borderRadius: 20,
              letterSpacing: "0.7px",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {badge.label}
          </span>
        )}
      </div>

      <div
        style={{
          width: "100%",
          height: 1,
          background: `linear-gradient(90deg,${accent}26 0%,transparent 100%)`,
          marginBottom: 8,
        }}
      />

      {data.sub && (
        <p
          style={{
            fontSize: 9.5,
            color: "#4a6080",
            margin: 0,
            fontWeight: 500,
            lineHeight: 1.6,
            paddingRight: 4,
          }}
        >
          {data.sub}
        </p>
      )}

      {data.route && <ClickHereLink accent={accent} hov={hov} />}

      <Handle type="source" position={Position.Bottom} style={SOURCE_HANDLE} />
    </div>
  );
};

const CardNode = ({ data }) => {
  const [hov, setHov] = useState(false);
  const color = data.color || {
    bg: "#fff",
    border: "#cbd5e1",
    text: T.dark,
    dot: T.primary,
    icon: T.primary,
  };

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: data.width,
        border: "1.5px solid #e8edf3",
        borderBottom: "2.5px solid #e8edf3",
        borderRadius: 12,
        boxShadow:
          hov && data.route
            ? `0 0 0 1.5px ${color.border}, 0 10px 22px -7px ${color.dot}28`
            : "0 1px 7px -1px rgba(0,0,0,0.05)",
        background:
          hov && data.route
            ? `linear-gradient(145deg,${color.bg} 0%,#ffffff 100%)`
            : "#ffffff",
        padding: "11px 13px 10px",
        textAlign: "center",
        cursor: data.route ? "pointer" : "default",
        transition:
          "box-shadow 0.22s ease, background 0.22s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        transform:
          hov && data.route
            ? "translateY(-4px) scale(1.02)"
            : "translateY(0) scale(1)",
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0,
      }}
    >
      <Handle type="target" position={Position.Top} style={TARGET_HANDLE} />

      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 28,
          height: 2.5,
          borderRadius: "0 0 4px 4px",
          background: color.dot,
          opacity: hov ? 0.95 : 0.5,
          transition: "opacity 0.2s ease",
        }}
      />

      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: `radial-gradient(circle at 35% 35%,${color.dot}ee,${color.border})`,
          display: "block",
          marginBottom: 6,
          marginTop: 6,
          boxShadow: `0 1px 4px ${color.dot}50`,
        }}
      />

      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          color: color.text,
          lineHeight: 1.25,
          letterSpacing: "-0.1px",
        }}
      >
        {data.title}
      </div>

      {data.sub && (
        <div
          style={{
            fontSize: 8.5,
            color: color.text,
            opacity: 0.6,
            marginTop: 4,
            fontWeight: 500,
            lineHeight: 1.45,
          }}
        >
          {data.sub}
        </div>
      )}

      {data.route && <ClickHereLink accent={color.border} hov={hov} small />}

      <Handle type="source" position={Position.Bottom} style={SOURCE_HANDLE} />
    </div>
  );
};

const nodeTypes = {
  groupNode: GroupNode,
  cardNode: CardNode,
};

const initialNodes = [
  {
    id: "setup-cargo",
    type: "groupNode",
    position: { x: 275, y: 0 },
    data: {
      title: "Create Cargo",
      sub: "Manage cargo orders. Go to More Actions to generate cargo-cargo, cargo analysis, or My Matrix estimations.",
      width: 270,
      height: 115,
      route: "/orders",
      color: C.teal.border,
    },
  },
  {
    id: "setup-vessel",
    type: "groupNode",
    position: { x: 565, y: 0 },
    data: {
      title: "Create Vessel",
      sub: "Manage fleet profiles. Go to More Actions to generate vessel-vessel or My Matrix estimations.",
      width: 270,
      height: 115,
      route: "/vesselManager",
      color: C.coral.border,
    },
  },
  {
    id: "cargo-cargo-est",
    type: "cardNode",
    position: { x: 185, y: 240 },
    data: {
      title: "Create Cargo-Cargo Estimation",
      sub: "Go to More Actions → Create Cargo Estimation",
      color: C.teal,
      route: "/orders",
      width: 160,
    },
  },
  {
    id: "cargo-analysis-est",
    type: "cardNode",
    position: { x: -70, y: 240 },
    data: {
      title: "Create Cargo Analysis",
      sub: "Go to More Actions → Create Cargo Analysis",
      color: C.amber,
      route: "/orders",
      width: 160,
    },
  },
  {
    id: "my-matrix",
    type: "cardNode",
    position: { x: 475, y: 240 },
    data: {
      title: "My Matrix",
      sub: "Go to More Actions → Create Vessel Cargo Estimation",
      color: C.purple,
      route: "/calculator",
      width: 160,
    },
  },
  {
    id: "vessel-vessel-est",
    type: "cardNode",
    position: { x: 765, y: 240 },
    data: {
      title: "Create Vessel-Vessel Estimation",
      sub: "Go to More Actions → Create Vessel Estimation",
      color: C.coral,
      route: "/vesselManager",
      width: 160,
    },
  },

  {
    id: "subtab-cargo-cargo",
    type: "cardNode",
    position: { x: 185, y: 460 },
    data: {
      title: "Cargo-Cargo Est",
      sub: "Manage specific Cargo-to-Cargo estimation records.",
      color: C.teal,
      route: "/cargo-cargo-matrix",
      width: 160,
    },
  },
  {
    id: "subtab-cargo-analysis",
    type: "cardNode",
    position: { x: -70, y: 460 },
    data: {
      title: "Cargo Analysis",
      sub: "Manage and view specific cargo analysis reports.",
      color: C.amber,
      route: "/cargo-analysis",
      width: 160,
    },
  },
  {
    id: "subtab-all-est",
    type: "cardNode",
    position: { x: 475, y: 460 },
    data: {
      title: "All Estimation",
      sub: "Consolidated view of all estimations, including My Matrix calculations.",
      color: C.purple,
      route: "/all-estimation",
      width: 160,
    },
  },
  {
    id: "subtab-vessel-vessel",
    type: "cardNode",
    position: { x: 765, y: 460 },
    data: {
      title: "Vessel-Vessel Est",
      sub: "Manage specific Vessel-to-Vessel estimation records.",
      color: C.coral,
      route: "/vessel-vessel-matrix",
      width: 160,
    },
  },
];

const tealEdge = {
  type: "bezier",
  animated: true,
  style: { stroke: C.teal.dot, strokeWidth: 1.6, strokeDasharray: "5 3" },
  markerEnd: { type: MarkerType.ArrowClosed, color: C.teal.dot },
};
const coralEdge = {
  type: "bezier",
  animated: true,
  style: { stroke: C.coral.dot, strokeWidth: 1.6, strokeDasharray: "5 3" },
  markerEnd: { type: MarkerType.ArrowClosed, color: C.coral.dot },
};
const purpleEdge = {
  type: "bezier",
  animated: true,
  style: { stroke: C.purple.dot, strokeWidth: 1.6, strokeDasharray: "5 3" },
  markerEnd: { type: MarkerType.ArrowClosed, color: C.purple.dot },
};
const amberEdge = {
  type: "bezier",
  animated: true,
  style: { stroke: C.amber.dot, strokeWidth: 1.6, strokeDasharray: "5 3" },
  markerEnd: { type: MarkerType.ArrowClosed, color: C.amber.dot },
};
const mergeEdge = {
  type: "bezier",
  animated: true,
  style: {
    stroke: "#94a3b8",
    strokeWidth: 1.2,
    strokeDasharray: "3 3",
    opacity: 0.5,
  },
  markerEnd: undefined,
};

const initialEdges = [
  {
    id: "e-cargo-to-cc-est",
    source: "setup-cargo",
    target: "cargo-cargo-est",
    ...tealEdge,
  },
  {
    id: "e-cargo-to-cargo-analysis",
    source: "setup-cargo",
    target: "cargo-analysis-est",
    ...amberEdge,
  },
  {
    id: "e-cargo-to-my-matrix",
    source: "setup-cargo",
    target: "my-matrix",
    ...purpleEdge,
  },
  {
    id: "e-vessel-to-my-matrix",
    source: "setup-vessel",
    target: "my-matrix",
    ...purpleEdge,
  },
  {
    id: "e-vessel-to-vv-est",
    source: "setup-vessel",
    target: "vessel-vessel-est",
    ...coralEdge,
  },

  {
    id: "e-cc-est-subtab",
    source: "cargo-cargo-est",
    target: "subtab-cargo-cargo",
    ...tealEdge,
  },
  {
    id: "e-cargo-analysis-subtab",
    source: "cargo-analysis-est",
    target: "subtab-cargo-analysis",
    ...amberEdge,
  },
  {
    id: "e-my-matrix-alltab",
    source: "my-matrix",
    target: "subtab-all-est",
    ...purpleEdge,
  },
  {
    id: "e-vv-est-subtab",
    source: "vessel-vessel-est",
    target: "subtab-vessel-vessel",
    ...coralEdge,
  },
  {
    id: "e-cc-est-alltab",
    source: "cargo-cargo-est",
    target: "subtab-all-est",
    ...mergeEdge,
  },
  {
    id: "e-vv-est-alltab",
    source: "vessel-vessel-est",
    target: "subtab-all-est",
    ...mergeEdge,
  },
];

export default function EstimationWorkflowDiagram({ goToRoute, onClose }) {
  const [nodes, , onNodesChange] = useNodesState(
    JSON.parse(JSON.stringify(initialNodes))
  );
  const [edges, , onEdgesChange] = useEdgesState(
    JSON.parse(JSON.stringify(initialEdges))
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 350);
    return () => clearTimeout(timer);
  }, []);

  const onNodeClick = (_, node) => {
    if (node.data?.route) {
      goToRoute(node.data.route);
      onClose();
    }
  };

  return (
    <div style={{ width: "100%", height: "75vh", position: "relative" }}>
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          onNodeClick={onNodeClick}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          style={{ background: "#f8fafc" }}
        >
          <Background variant="dots" color="#cbd5e1" gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      )}
    </div>
  );
}
